import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Marker, useMap, useMapEvents } from "react-leaflet";
import L, {
  LatLng,
  LatLngBounds,
  LatLngBoundsExpression,
  LatLngExpression,
  LatLngTuple,
} from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box } from "@mui/material";
import { GenerationUnit, ProjectOverviewItem } from "../../../../types";
import { areasApi } from "../../../../services/api/areas";
import { parcelsApi } from "../../../../services/api/parcels";
import { generationUnitsApi } from "../../../../services/api/generationUnits";
import {
  BaseMap,
  ProjectAreaLayer,
  LandParcelLayer,
  WindTurbinesLayer,
  createWindmillIcon,
  WINDMILL_ICON_HEIGHT,
} from "../../../map";
import type { ParcelData } from "../../../map";
import { GERMANY_CENTER } from "../../../../utils/geometry";
import ParcelPopup from "../../projectArea/components/ParcelPopup";
import GenerationUnitPopup from "../../projectGenerationUnits/components/GenerationUnitPopup";

const DETAIL_ZOOM_THRESHOLD = 12;

interface OverviewMapSectionProps {
  projects: ProjectOverviewItem[];
  onProjectSelect: (id: string) => void;
  selectedProjectId?: string;
  /**
   * Projekt im Fokus: Die Karte zeigt nur noch dieses und zoomt auf seine Fläche bzw. ohne
   * Fläche auf seinen Punkt. Die Windräder aller Projekte blenden dabei aus; nur ein Projekt im
   * Fokus ohne Fläche und ohne Anlagen mit Standort behält seins. Ohne Fokus wieder alle Projekte.
   */
  focusProjectId?: string | null;
  height?: string;
}

/** Zoom auf ein Projekt ohne Fläche – nah genug, dass seine Anlagen erscheinen */
const POINT_FOCUS_ZOOM = 14;
/**
 * Dauer des Flugs zum Projekt in s. Lang genug für einen ruhigen Übergang über bis zu acht
 * Zoomstufen, kurz genug für ↑/↓ durch die Liste.
 */
const FLY_DURATION = 1.3;
/** Aus- und Einblenden der Windräder in s – vor dem Flug ins Projekt bzw. nach dem Rückflug */
const WINDMILL_FADE = 0.4;
/** Einblenden von Fläche, Flurstücken und Anlagen, sobald die Karte steht, in s */
const DETAIL_FADE_IN = 0.4;

/** Flurstück oder Anlage, deren Popup offen ist */
interface MapSelection {
  kind: "parcel" | "unit";
  projectId: string;
  id: string;
  position: LatLngTuple;
  /**
   * Zählt jeden Klick. Das Popup hängt daran: Ein zweiter Klick auf dasselbe Flurstück öffnet
   * es neu, nachdem Leaflet es beim Klick in die Karte schon geschlossen hat.
   */
  seq: number;
}

interface ProjectDetailData {
  areaCoords: [number, number][];
  parcels: ParcelData[];
  anlagen: GenerationUnit[];
}

function geojsonToLeaflet(geometrie: {
  type: string;
  coordinates: number[][][] | number[][][][];
}): [number, number][] {
  let ring: number[][];
  if (geometrie.type === "Polygon") {
    ring = (geometrie.coordinates as number[][][])[0];
  } else {
    ring = (geometrie.coordinates as number[][][][])[0][0];
  }
  return ring.map(([lon, lat]) => [lat, lon] as [number, number]);
}

// Passt die Kartenansicht beim ersten Render an alle Marker an
function BoundsController({
  bounds,
}: {
  bounds: LatLngBoundsExpression | null;
}) {
  const map = useMap();
  const hasFitted = useRef(false);
  useEffect(() => {
    if (bounds && !hasFitted.current) {
      // Oben zusätzlich die Höhe des Windrads: Es ragt über seinen Punkt hinaus und würde bei den
      // nördlichsten Projekten sonst vom Kartenrand abgeschnitten.
      map.fitBounds(bounds, {
        paddingTopLeft: [60, 60 + WINDMILL_ICON_HEIGHT],
        paddingBottomRight: [60, 60],
        maxZoom: 12,
      });
      hasFitted.current = true;
    }
  }, [map, bounds]);
  return null;
}

/**
 * Zoomt auf das Projekt im Fokus – auf seine Fläche bzw. ohne Fläche auf seinen Punkt; ohne
 * Fokus zurück auf alle Projekte.
 *
 * Der Übergang ins Projekt läuft nacheinander: Erst blenden die Windräder aus, währenddessen
 * laden die Daten des Projekts, dann fliegt die Karte – einmal, direkt zum Ziel. Ohne die
 * Daten (`areaCoords` noch `null`) flöge sie zum Punkt und mitten im Flug ein zweites Mal zur
 * Fläche. Wechselt der Fokus mit ↑/↓ von Projekt zu Projekt, sind die Windräder schon weg und
 * die Karte fliegt, sobald die Daten da sind.
 *
 * `onFlyingChange` meldet Start und Ende des Flugs. Währenddessen zeichnet Leaflet Vektoren nicht
 * neu, sondern vergrößert das bisherige Bild – eine Fläche von wenigen Pixeln würde zum
 * verwaschenen Fleck. Die Detail-Ebenen stehen deshalb erst, wenn die Karte steht.
 */
function FocusController({
  focus,
  areaCoords,
  allBounds,
  onFlyingChange,
}: {
  focus: ProjectOverviewItem | null;
  areaCoords: [number, number][] | null;
  allBounds: LatLngBoundsExpression | null;
  onFlyingChange: (flying: boolean) => void;
}) {
  const map = useMap();
  const hadFocus = useRef(false);
  const previousFocusId = useRef<string | null>(null);
  /** Zeitpunkt, bis zu dem die Windräder noch ausblenden */
  const fadeDoneAt = useRef(0);
  useEffect(() => {
    const focusId = focus?.id ?? null;
    if (focusId !== previousFocusId.current) {
      if (focusId && !previousFocusId.current) {
        fadeDoneAt.current = Date.now() + WINDMILL_FADE * 1000;
      }
      previousFocusId.current = focusId;
    }

    // Endet auch, wenn der Flug abbricht, etwa weil man die Karte zieht: Dann meldet das
    // Ziehen das Ende.
    const startFlight = () => {
      onFlyingChange(true);
      map.once("moveend", () => onFlyingChange(false));
    };
    if (focus) {
      if (areaCoords === null) return;
      const timer = window.setTimeout(
        () => {
          hadFocus.current = true;
          startFlight();
          if (areaCoords.length > 0) {
            map.flyToBounds(areaCoords, {
              padding: [40, 40],
              duration: FLY_DURATION,
            });
          } else if (focus.koordinate) {
            map.flyTo(
              [focus.koordinate.breitengrad, focus.koordinate.laengengrad],
              POINT_FOCUS_ZOOM,
              { duration: FLY_DURATION },
            );
          }
        },
        Math.max(0, fadeDoneAt.current - Date.now()),
      );
      return () => window.clearTimeout(timer);
    } else if (hadFocus.current && allBounds) {
      hadFocus.current = false;
      startFlight();
      map.flyToBounds(allBounds, {
        paddingTopLeft: [60, 60 + WINDMILL_ICON_HEIGHT],
        paddingBottomRight: [60, 60],
        maxZoom: 12,
        duration: FLY_DURATION,
      });
    }
  }, [map, focus, areaCoords, allBounds, onFlyingChange]);
  return null;
}

// Meldet Zoom- und Bounds-Änderungen nach außen
function ZoomBoundsController({
  onUpdate,
}: {
  onUpdate: (zoom: number, bounds: LatLngBounds) => void;
}) {
  useMapEvents({
    zoomend: (e) => onUpdate(e.target.getZoom(), e.target.getBounds()),
    moveend: (e) => onUpdate(e.target.getZoom(), e.target.getBounds()),
  });
  return null;
}

const OverviewMapSection: React.FC<OverviewMapSectionProps> = ({
  projects,
  onProjectSelect,
  selectedProjectId,
  focusProjectId,
  height = "50vh",
}) => {
  const [zoom, setZoom] = useState(6);
  const [mapBounds, setMapBounds] = useState<LatLngBounds | null>(null);
  const [flying, setFlying] = useState(false);
  const [selection, setSelection] = useState<MapSelection | null>(null);
  const selectionSeq = useRef(0);

  // Wechselt das Projekt im Fokus, gehört das Popup nicht mehr dazu
  const [selectionFocusId, setSelectionFocusId] = useState(focusProjectId);
  if (selectionFocusId !== focusProjectId) {
    setSelectionFocusId(focusProjectId);
    setSelection(null);
  }

  /** Mit dem Flug schließt das Popup – die Stelle, an der es steht, blendet aus */
  const handleFlyingChange = useCallback((isFlying: boolean) => {
    setFlying(isFlying);
    if (isFlying) setSelection(null);
  }, []);

  const select = useCallback(
    (
      kind: MapSelection["kind"],
      projectId: string,
      id: string,
      position: LatLngTuple,
    ) => {
      selectionSeq.current += 1;
      setSelection({ kind, projectId, id, position, seq: selectionSeq.current });
    },
    [],
  );

  /**
   * Leaflet hat das Popup geschlossen. Nur das aktuelle zählt: Ein Klick auf ein anderes
   * Flurstück schließt erst das alte Popup und öffnet dann das neue.
   */
  const closeSelection = useCallback((seq: number) => {
    setSelection((current) => (current?.seq === seq ? null : current));
  }, []);

  const projectDataCache = useRef<Map<string, ProjectDetailData>>(new Map());
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());

  const allProjectsOnMap = useMemo(
    () => projects.filter((p) => p.koordinate !== null),
    [projects],
  );
  const focusProject = useMemo(
    () => allProjectsOnMap.find((p) => p.id === focusProjectId) ?? null,
    [allProjectsOnMap, focusProjectId],
  );

  const initialBounds: LatLngBoundsExpression | null = useMemo(
    () =>
      allProjectsOnMap.length > 0
        ? (allProjectsOnMap.map(
            (p) =>
              [
                p.koordinate!.breitengrad,
                p.koordinate!.laengengrad,
              ] as LatLngExpression,
          ) as LatLngBoundsExpression)
        : null,
    [allProjectsOnMap],
  );

  const handleMapUpdate = useCallback(
    (newZoom: number, newBounds: LatLngBounds) => {
      setZoom(newZoom);
      setMapBounds(newBounds);
    },
    [],
  );

  // Projekte, die im Detail angezeigt werden sollen (Zoom-Schwelle + im Viewport); das Projekt
  // im Fokus immer – seine Fläche bestimmt erst, wohin gezoomt wird.
  const detailProjects = useMemo(() => {
    if (focusProject) return [focusProject];
    if (zoom < DETAIL_ZOOM_THRESHOLD || !mapBounds) return [];
    return allProjectsOnMap.filter((p) =>
      mapBounds.contains([
        p.koordinate!.breitengrad,
        p.koordinate!.laengengrad,
      ]),
    );
  }, [zoom, mapBounds, allProjectsOnMap, focusProject]);

  // Projekte, für die eine Fläche geladen wurde (nur diese ersetzen den Marker)
  const loadedDetailIds = useMemo(
    () =>
      new Set(
        detailProjects
          .filter((p) => loadedIds.has(p.id))
          .filter((p) => {
            const data = projectDataCache.current.get(p.id);
            return !!data && data.areaCoords.length > 0;
          })
          .map((p) => p.id),
      ),
    [detailProjects, loadedIds],
  );

  // Lazy-Fetch: nur fehlende Projekte im Viewport laden
  useEffect(() => {
    const toFetch = detailProjects.filter(
      (p) => !projectDataCache.current.has(p.id),
    );
    if (toFetch.length === 0) return;

    toFetch.forEach(async (project) => {
      try {
        const [areaData, parcelsData, anlagenData] = await Promise.all([
          areasApi.getProjectArea(project.id),
          parcelsApi.getParcels(project.id),
          generationUnitsApi.getProjectGenerationUnits(project.id),
        ]);
        projectDataCache.current.set(project.id, {
          areaCoords: areaData?.geometrie?.coordinates
            ? geojsonToLeaflet(areaData.geometrie)
            : [],
          parcels: parcelsData ?? [],
          anlagen: anlagenData ?? [],
        });
      } catch {
        projectDataCache.current.set(project.id, {
          areaCoords: [],
          parcels: [],
          anlagen: [],
        });
      }
      setLoadedIds(new Set(projectDataCache.current.keys()));
    });
  }, [detailProjects]);

  const focusData =
    focusProject && loadedIds.has(focusProject.id)
      ? (projectDataCache.current.get(focusProject.id) ?? null)
      : null;
  const focusAreaCoords = focusData?.areaCoords ?? null;

  /**
   * Ob das Windrad eines Projekts zu sehen ist. Während des Flugs keins – sie blenden erst ein,
   * wenn die Karte steht. Im Fokus nur das des Projekts selbst, und auch das nur, wenn es weder
   * Fläche noch Anlagen mit Standort hat – sonst zeigen die das Projekt. Anlagen ohne
   * Koordinaten zählen nicht: Sie erscheinen nicht auf der Karte, und ohne Windrad stünde dort
   * gar nichts. Ohne Fokus alle, bis auf Projekte, deren Fläche schon im Detail steht.
   */
  const windmillVisible = (project: ProjectOverviewItem): boolean =>
    !flying &&
    (focusProject
      ? project.id === focusProject.id &&
        !!focusData &&
        focusData.areaCoords.length === 0 &&
        !focusData.anlagen.some(
          (a) => a.breitengrad != null && a.laengengrad != null,
        )
      : !loadedDetailIds.has(project.id));

  /**
   * Popup des gewählten Flurstücks bzw. der Anlage – nur, solange ihr Projekt im Detail steht.
   * Zoomt man heraus, verschwinden Flurstücke und Anlagen und mit ihnen das Popup.
   */
  const selectionPopup = (() => {
    if (!selection) return null;
    if (!detailProjects.some((p) => p.id === selection.projectId)) return null;
    const data = projectDataCache.current.get(selection.projectId);
    const onClose = () => closeSelection(selection.seq);
    if (selection.kind === "parcel") {
      const parcel = data?.parcels.find((p) => p.id === selection.id);
      return parcel ? (
        <ParcelPopup
          key={selection.seq}
          projectId={selection.projectId}
          parcel={parcel}
          position={selection.position}
          onClose={onClose}
        />
      ) : null;
    }
    const unit = data?.anlagen.find((a) => a.id === selection.id);
    return unit ? (
      <GenerationUnitPopup
        key={selection.seq}
        projectId={selection.projectId}
        unit={unit}
        position={selection.position}
        onClose={onClose}
      />
    ) : null;
  })();

  if (allProjectsOnMap.length === 0) return null;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        // Windräder blenden weich aus und ein. Während Leaflet animiert zoomt, bleibt dessen
        // Übergang der Position erhalten – sonst sprängen die Marker.
        "& .leaflet-marker-icon": {
          transition: `opacity ${WINDMILL_FADE}s ease`,
        },
        "& .leaflet-zoom-anim .leaflet-marker-icon": {
          transition: `opacity ${WINDMILL_FADE}s ease, transform 0.25s cubic-bezier(0, 0, 0.25, 1)`,
        },
        // Fläche, Flurstücke und Anlagen erscheinen weich, sobald sie gezeichnet werden. Leaflet
        // behält die Pfade beim Neuzeichnen, das Einblenden läuft also nur beim Erscheinen.
        // Die Windräder der Projekte sind Bilder und bleiben davon unberührt.
        "@keyframes overview-detail-fade-in": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        "& .leaflet-overlay-pane path, & .leaflet-marker-pane path": {
          animation: `overview-detail-fade-in ${DETAIL_FADE_IN}s ease`,
        },
      }}
    >
      {/* Wer über der Karte scrollt, will meist die Seite scrollen – gezoomt wird über die
          Knöpfe, verschieben geht weiter per Ziehen */}
      <BaseMap
        height={height}
        zoom={6}
        center={GERMANY_CENTER}
        smoothFlights
        scrollWheelZoom={false}
        showZoomControl
      >
        <BoundsController bounds={initialBounds} />
        <FocusController
          focus={focusProject}
          areaCoords={focusAreaCoords}
          allBounds={initialBounds}
          onFlyingChange={handleFlyingChange}
        />
        <ZoomBoundsController onUpdate={handleMapUpdate} />

        {/* Windräder bleiben stehen und blenden nur aus – so verschwinden sie, während die
            Karte zum Projekt fliegt, und kehren ohne Fokus zurück. */}
        {allProjectsOnMap.map((project) => {
          const visible = windmillVisible(project);
          return (
            <Marker
              key={project.id}
              position={[
                project.koordinate!.breitengrad,
                project.koordinate!.laengengrad,
              ]}
              icon={createWindmillIcon(selectedProjectId === project.id)}
              opacity={visible ? 1 : 0}
              zIndexOffset={visible ? 0 : -1000}
              eventHandlers={{
                // Ein ausgeblendetes Windrad nimmt keinen Klick an.
                click: () => {
                  if (visible) onProjectSelect(project.id);
                },
              }}
            />
          );
        })}

        {/* Detail-Layer für sichtbare Projekte ab Zoom-Schwelle – nicht während des Flugs */}
        {!flying &&
          detailProjects.map((project) => {
            const data = projectDataCache.current.get(project.id);
            if (!data) return null;
            return (
              <React.Fragment key={project.id}>
                <ProjectAreaLayer
                  projectName={project.name}
                  flaecheCoordinates={data.areaCoords}
                />
                <LandParcelLayer
                  parcels={data.parcels}
                  layerVisibility={{ land_parcels: true }}
                  onParcelClick={(parcelId: string, latlng: LatLng) =>
                    select("parcel", project.id, parcelId, [
                      latlng.lat,
                      latlng.lng,
                    ])
                  }
                  selectedParcelId={
                    selection?.kind === "parcel" &&
                    selection.projectId === project.id
                      ? selection.id
                      : undefined
                  }
                />
                <WindTurbinesLayer
                  anlagen={data.anlagen}
                  visible={true}
                  flaecheCoordinates={
                    data.areaCoords.length > 0 ? data.areaCoords : undefined
                  }
                  readOnly
                  onSelect={(unitId) => {
                    const unit = data.anlagen.find((a) => a.id === unitId);
                    if (unit?.breitengrad == null || unit.laengengrad == null)
                      return;
                    select("unit", project.id, unitId, [
                      unit.breitengrad,
                      unit.laengengrad,
                    ]);
                  }}
                  selectedId={
                    selection?.kind === "unit" &&
                    selection.projectId === project.id
                      ? selection.id
                      : undefined
                  }
                />
              </React.Fragment>
            );
          })}

        {!flying && selectionPopup}
      </BaseMap>
    </Box>
  );
};

export default OverviewMapSection;
